import {
  Button,
  Callout,
  Card,
  Classes,
  FormGroup,
  InputGroup,
  MenuItem,
  NumericInput,
  ProgressBar,
  Switch,
} from "@blueprintjs/core";
import {
  DateInput3,
  DateRangeInput3,
  type DateRangeInput3Props,
} from "@blueprintjs/datetime2";
import { MultiSelect, Select } from "@blueprintjs/select";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  CreateScheduledEventDocument,
  DeleteScheduledEventDocument,
  GetScheduledEventDocument,
  GetScheduledEventsDocument,
  type ScheduledEvent,
  ScheduledEventModificationType,
  UpdateScheduledEventDocument,
  useGetScheduledEventQuery,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import { produce, type WritableDraft } from "immer";
import { uniq } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Frequency, RRule } from "rrule";
import {
  EDIT_CARD_WIDTH_CLASS,
  MonthEnum,
  MonthEnumNames,
  SidebarEntryPath,
  WeekdayEnum,
  WeekdayEnumNames,
} from "../../../../constants/contants";
import { useAppSelector } from "../../../../store/store";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";
import {
  createScheduledEventMutationVariables,
  createScheduledEventRecurringSettingsFromRRule,
  updateScheduledEventMutationVariables,
} from "./calendar-utils";
import { SchedulerEditActionList } from "./SchedulerEditAction";

type SchedulerEditProps = {
  modifyType: ScheduledEventModificationType;
};

const defaultScheduledEvent: ScheduledEvent = {
  start: moment().startOf("day").toDate(),
  name: "",
  organization: "",
  startProcessed: false,
  endProcessed: false,
  timezone: "America/Chicago",
};

export function SchedulerEdit({
  modifyType = ScheduledEventModificationType.Single,
}: SchedulerEditProps) {
  const [event, setEvent] = useState<ScheduledEvent>(defaultScheduledEvent);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [recurring, setRecurring] = useState<boolean>(!!event.recurring);
  const { id } = useParams();
  const argsCreate = useAppSelector((state) => state.scheduler.argsCreate);
  const rrule = useMemo<RRule>(
    () =>
      new RRule({
        bymonth: event.recurring?.bymonth,
        byweekday: event.recurring?.byweekday,
        count: undefined,
        dtstart: moment(event.start ?? new Date())?.toDate(),
        freq: event.recurring?.freq ?? RRule.DAILY,
        interval: event.recurring?.interval ?? 1,
        tzid: event.timezone,
        until: moment(event.recurring?.until ?? new Date()).toDate(),
      }),
    [
      event.recurring?.bymonth,
      event.recurring?.byweekday,
      event.recurring?.freq,
      event.recurring?.interval,
      event.recurring?.until,
      event.start,
      event.timezone,
    ],
  );

  const eventCreateArgs = useMemo(
    () => createScheduledEventMutationVariables(event),
    [event],
  );
  const eventUpdateArgs = useMemo(
    () => updateScheduledEventMutationVariables(event, modifyType),
    [event, modifyType],
  );

  const updateEvent = useCallback(
    (callback: (draft: WritableDraft<ScheduledEvent>) => void) => {
      setEvent((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataEvent,
    loading: loadingEvent,
    error: errorEvent,
  } = useGetScheduledEventQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  const dateRangeValue = useMemo<DateRangeInput3Props["value"]>(
    () => [
      event.start ? new Date(event.start) : null,
      event.end ? new Date(event.end) : null,
    ],
    [event.end, event.start],
  );
  const showRecurring = useMemo<boolean>(
    () =>
      !id ||
      (modifyType === ScheduledEventModificationType.All &&
        !!dataEvent?.item?.groupId),
    [dataEvent?.item?.groupId, id, modifyType],
  );

  useEffect(
    function effectOnArgsCreateChanged() {
      if (argsCreate) {
        setEvent({
          start: moment(argsCreate.start).toDate(),
          end: argsCreate.end ? moment(argsCreate.end).toDate() : undefined,
          organization: "",
          startProcessed: false,
          endProcessed: false,
          timezone: "America/Chicago",
        });
      }
    },
    [argsCreate],
  );

  useEffect(
    function effectOnEditDataChanged() {
      if (dataEvent?.item) {
        setEvent({
          ...dataEvent?.item,
          start: dataEvent?.item?.start
            ? moment(dataEvent?.item?.start).toDate()
            : undefined,
          end: dataEvent?.item?.end
            ? moment(dataEvent?.item?.end).toDate()
            : undefined,
        });
      }
    },
    [dataEvent],
  );

  useEffect(
    function effectOnRecurringChanged() {
      if (event.recurring) {
        setRecurring(true);
      }
    },
    [event.recurring],
  );

  useEffect(
    function effectOnRRuleChanged() {
      updateEvent((draft) => {
        draft.recurring = recurring
          ? createScheduledEventRecurringSettingsFromRRule(rrule)
          : null;
      });
    },
    [recurring, rrule, updateEvent],
  );

  useUpdateSelectedOrganization(dataEvent?.item?.organization);

  if (loadingEvent) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorEvent) {
    return (
      <Callout intent="danger" title="Failed Loading Event">
        <p>Failed loading event item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingEvent}
        title={
          modifyType === ScheduledEventModificationType.Single
            ? "Event"
            : "Event with All Recurring Events"
        }
        name={event?.name}
      />
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={event.name ?? ""}
              onValueChange={(value) =>
                updateEvent((draft) => {
                  draft.name = value;
                })
              }
            />
          </FormGroup>
          <FormGroup disabled={disabled}>
            <Switch
              disabled={disabled}
              label="Enable Date Duration with an Ending Action"
              checked={!!event.end}
              onChange={() =>
                updateEvent((draft) => {
                  if (draft.end) {
                    draft.end = undefined;
                    draft.endAction = undefined;
                  } else {
                    draft.end = draft.end ? undefined : moment().endOf("day");
                  }
                })
              }
            />
          </FormGroup>
          {!event.end && (
            <FormGroup label="Date" disabled={disabled}>
              <DateInput3
                closeOnSelection
                dateFnsFormat="LLL do, yyyy 'at' K:mm a"
                disabled={disabled}
                fill
                highlightCurrentDay
                onChange={(date) => {
                  updateEvent((draft) => {
                    draft.start = (date && new Date(date)) || undefined;
                    draft.end = undefined;
                    draft.endAction = undefined;
                  });
                }}
                popoverProps={{
                  minimal: true,
                }}
                shortcuts
                timePrecision="second"
                value={event.start?.toISOString() ?? undefined}
              />
            </FormGroup>
          )}
          {!!event.end && (
            <FormGroup label="Date Range" disabled={disabled}>
              <DateRangeInput3
                allowSingleDayRange
                closeOnSelection
                contiguousCalendarMonths
                dateFnsFormat="LLL do, yyyy 'at' K:mm a"
                disabled={disabled}
                fill
                highlightCurrentDay
                onChange={(range) => {
                  updateEvent((draft) => {
                    draft.start = range[0] ?? undefined;
                    draft.end = range[1] ?? undefined;
                  });
                }}
                popoverProps={{
                  minimal: true,
                }}
                shortcuts
                timePrecision="second"
                value={dateRangeValue}
              />
            </FormGroup>
          )}
        </div>
      </Card>
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            disabled={disabled}
            label={event.end ? "Starting Actions" : "Actions"}
          >
            <SchedulerEditActionList
              disabled={disabled}
              actions={event.startAction}
              onChange={(actions) => {
                updateEvent((draft) => {
                  draft.startAction = actions;
                });
              }}
            />
          </FormGroup>
        </div>
      </Card>
      {!!event.end && (
        <Card elevation={1} className="mt-4">
          <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
            <FormGroup disabled={disabled} label="Ending Actions">
              <SchedulerEditActionList
                disabled={disabled}
                actions={event.endAction}
                onChange={(actions) => {
                  updateEvent((draft) => {
                    draft.endAction = actions;
                  });
                }}
              />
            </FormGroup>
          </div>
        </Card>
      )}
      {showRecurring && (
        <Card elevation={1} className="mt-4">
          <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
            <FormGroup disabled={disabled}>
              <Switch
                disabled={disabled}
                label="Recurring Event"
                checked={recurring}
                onChange={() => {
                  setRecurring(!recurring);
                }}
              />
            </FormGroup>
            {recurring && (
              <>
                <FormGroup disabled={disabled}>
                  <h5 className={Classes.HEADING}>
                    Scheduled {rrule.toText()}
                  </h5>
                </FormGroup>
                <FormGroup disabled={disabled} label="Frequency">
                  <Select<Frequency>
                    activeItem={event.recurring?.freq}
                    disabled={disabled}
                    fill
                    filterable={false}
                    items={[
                      Frequency.YEARLY,
                      Frequency.MONTHLY,
                      Frequency.WEEKLY,
                      Frequency.DAILY,
                      Frequency.HOURLY,
                    ]}
                    itemRenderer={(
                      frequency,
                      { handleClick, handleFocus, modifiers },
                    ) => (
                      <MenuItem
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        key={frequency}
                        onClick={handleClick}
                        onFocus={handleFocus}
                        roleStructure="listoption"
                        text={Frequency[frequency]}
                      />
                    )}
                    popoverProps={{ minimal: true }}
                    onItemSelect={(frequency) =>
                      updateEvent((draft) => {
                        draft.recurring = {
                          ...draft.recurring,
                          freq: frequency,
                        };
                      })
                    }
                  >
                    <Button
                      alignText="left"
                      disabled={disabled}
                      fill
                      minimal
                      outlined
                      rightIcon="caret-down"
                      text={
                        typeof event.recurring?.freq === "number"
                          ? RRule.FREQUENCIES[event.recurring.freq]
                          : "Select a Frequency"
                      }
                    />
                  </Select>
                </FormGroup>
                <FormGroup disabled={disabled} label="Weekdays">
                  <MultiSelect<WeekdayEnum>
                    disabled={disabled}
                    fill
                    items={Object.values(WeekdayEnum).filter(
                      (w) => typeof w === "number",
                    )}
                    itemsEqual={(a, b) => a === b}
                    itemPredicate={(query, weekday) =>
                      WeekdayEnumNames[weekday]
                        ?.toLocaleLowerCase()
                        ?.includes(query.toLocaleLowerCase()) ?? false
                    }
                    itemRenderer={(
                      weekday,
                      { handleClick, handleFocus, modifiers },
                    ) => (
                      <MenuItem
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        key={weekday}
                        onClick={handleClick}
                        onFocus={handleFocus}
                        roleStructure="listoption"
                        selected={event.recurring?.byweekday?.includes(weekday)}
                        shouldDismissPopover={false}
                        text={WeekdayEnumNames[weekday]}
                      />
                    )}
                    onItemSelect={(weekday) => {
                      updateEvent((draft) => {
                        if (draft.recurring?.byweekday?.includes(weekday)) {
                          draft.recurring = {
                            ...draft.recurring,
                            byweekday: draft.recurring.byweekday.filter(
                              (w) => w !== weekday,
                            ),
                          };
                        } else {
                          draft.recurring = {
                            ...draft.recurring,
                            byweekday: uniq(
                              [weekday].concat(
                                draft.recurring?.byweekday ?? [],
                              ),
                            ),
                          };
                        }
                      });
                    }}
                    popoverProps={{
                      minimal: true,
                    }}
                    selectedItems={event.recurring?.byweekday ?? []}
                    tagRenderer={(weekday) => WeekdayEnumNames[weekday]}
                    tagInputProps={{
                      autoResize: true,
                      className: "max-h-52 overflow-auto",
                      onRemove: (_, index) => {
                        const weekday = event.recurring?.byweekday?.[index];
                        if (typeof weekday === "number") {
                          updateEvent((draft) => {
                            if (Array.isArray(draft.recurring?.byweekday)) {
                              draft.recurring.byweekday.splice(index, 1);
                            }
                          });
                        }
                      },
                      tagProps: {
                        fill: true,
                        intent: "none",
                        minimal: true,
                      },
                    }}
                  />
                </FormGroup>
                <FormGroup disabled={disabled} label="Months">
                  <MultiSelect<MonthEnum>
                    disabled={disabled}
                    fill
                    items={Object.values(MonthEnum).filter(
                      (w) => typeof w === "number",
                    )}
                    itemsEqual={(a, b) => a === b}
                    itemPredicate={(query, month) =>
                      MonthEnumNames[month]
                        ?.toLocaleLowerCase()
                        ?.includes(query.toLocaleLowerCase()) ?? false
                    }
                    itemRenderer={(
                      month,
                      { handleClick, handleFocus, modifiers },
                    ) => (
                      <MenuItem
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        key={month}
                        onClick={handleClick}
                        onFocus={handleFocus}
                        roleStructure="listoption"
                        selected={event.recurring?.bymonth?.includes(month)}
                        shouldDismissPopover={false}
                        text={MonthEnumNames[month]}
                      />
                    )}
                    onItemSelect={(month) => {
                      updateEvent((draft) => {
                        if (draft.recurring?.bymonth?.includes(month)) {
                          draft.recurring = {
                            ...draft.recurring,
                            bymonth: draft.recurring.bymonth.filter(
                              (w) => w !== month,
                            ),
                          };
                        } else {
                          draft.recurring = {
                            ...draft.recurring,
                            bymonth: uniq(
                              [month].concat(draft.recurring?.bymonth ?? []),
                            ),
                          };
                        }
                      });
                    }}
                    popoverProps={{
                      minimal: true,
                    }}
                    selectedItems={event.recurring?.bymonth ?? []}
                    tagRenderer={(month) => MonthEnumNames[month]}
                    tagInputProps={{
                      autoResize: true,
                      className: "max-h-52 overflow-auto",
                      onRemove: (_, index) => {
                        const month = event.recurring?.bymonth?.[index];
                        if (typeof month === "number") {
                          updateEvent((draft) => {
                            if (Array.isArray(draft.recurring?.bymonth)) {
                              draft.recurring.bymonth.splice(index, 1);
                            }
                          });
                        }
                      },
                      tagProps: {
                        fill: true,
                        intent: "none",
                        minimal: true,
                      },
                    }}
                  />
                </FormGroup>
                <FormGroup
                  disabled={disabled}
                  label="Interval"
                  helperText="Number of [frequency] to skip before event is scheduled"
                >
                  <NumericInput
                    min={0}
                    max={500}
                    allowNumericCharactersOnly
                    clampValueOnBlur
                    disabled={disabled}
                    fill
                    value={event.recurring?.interval ?? 0}
                    onValueChange={(value) => {
                      updateEvent((draft) => {
                        draft.recurring = {
                          ...draft.recurring,
                          interval: value,
                        };
                      });
                    }}
                  />
                </FormGroup>
                <FormGroup label="Until Date" disabled={disabled}>
                  <DateInput3
                    canClearSelection
                    closeOnSelection
                    dateFnsFormat="LLL do, yyyy 'at' K:mm a"
                    defaultTimezone="America/Chicago"
                    disabled={disabled}
                    fill
                    highlightCurrentDay
                    onChange={(date) => {
                      updateEvent((draft) => {
                        draft.recurring = {
                          ...draft.recurring,
                          until: date,
                        };
                      });
                    }}
                    onTimezoneChange={(zone) => {
                      updateEvent((draft) => {
                        draft.recurring = {
                          ...draft.recurring,
                          tzid: zone,
                        };
                      });
                    }}
                    placeholder="Date & Time"
                    popoverProps={{ minimal: true }}
                    shortcuts
                    showTimezoneSelect
                    timePrecision="second"
                    timezone={event.recurring?.tzid ?? "America/Chicago"}
                    value={event.recurring?.until}
                  />
                </FormGroup>
                <FormGroup
                  disabled={disabled}
                  label="Max Number of Recurring Events"
                  labelInfo="(optional)"
                  helperText="Limits the number of events created for the schedule"
                >
                  <NumericInput
                    allowNumericCharactersOnly
                    clampValueOnBlur
                    disabled={disabled}
                    fill
                    max={1000}
                    min={0}
                    selectAllOnFocus
                    value={event.recurring?.count ?? undefined}
                    onValueChange={(value, valueAsString) => {
                      updateEvent((draft) => {
                        draft.recurring = {
                          ...draft.recurring,
                          count: valueAsString.length === 0 ? undefined : value,
                        };
                      });
                    }}
                  />
                </FormGroup>
                <FormGroup disabled={disabled} label="Example of Schedule">
                  <hr className="mb-2" />
                  {rrule
                    .all()
                    .slice(0, 10)
                    .map((d, n) => (
                      <div key={n} className="flex flex-row">
                        <div className="w-32">
                          {moment(d ?? new Date()).format("ddd")}
                        </div>
                        <div className="w-48">
                          {moment(d ?? new Date()).format("MMM Do YYYY")}
                        </div>
                        <div className="flex grow">
                          {moment(d ?? new Date()).format("h:mm:ss a")}
                        </div>
                      </div>
                    ))}
                </FormGroup>
              </>
            )}
          </div>
        </Card>
      )}
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreateScheduledEventDocument}
            documentDelete={DeleteScheduledEventDocument}
            documentUpdate={UpdateScheduledEventDocument}
            path={SidebarEntryPath.scheduler}
            isNew={!event?.id}
            refetchQueries={[
              GetScheduledEventDocument,
              GetScheduledEventsDocument,
            ]}
            title="Event"
            variablesCreate={eventCreateArgs}
            variablesUpdate={eventUpdateArgs}
            variablesDelete={{ id: event?.id }}
            onInflight={(inflight) => {
              setDisabled(inflight);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
